import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import { PageHeader, Card, Button, Badge, LoadingState, Alert } from '../components/ui';
import projectService, {
  getAvailableProvisioningAccounts,
} from '../services/projectService';
import AutoProvisionModal from '../components/ProjectDetail/ProjectForm/AutoProvisionModal';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const Projects = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [autoProvisionOpen, setAutoProvisionOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState(null); // { kind, project, accountId?, plan?, error?, success?, confirm? }
  
  // Fetch all projects
  const {
    data: projects,
    isLoading,
    error,
    refetch
  } = useQuery(
    'projects',
    () => projectService.getProjects(),
    {
      retry: 1,
      onError: (err) => {
        console.error('Error fetching projects:', err);
      }
    }
  );

  // Lazy-loaded list of provisioning-eligible accounts for the
  // promote/attach action dialogs. Loaded once the dialog opens.
  const accountsQuery = useQuery({
    queryKey: ['provisioning-accounts'],
    queryFn: getAvailableProvisioningAccounts,
    enabled: Boolean(actionDialog && (actionDialog.kind === 'promote' || actionDialog.kind === 'attach')),
    staleTime: 60_000,
  });
  const accounts = accountsQuery.data ? accountsQuery.data.data || [] : [];

  // Filter projects based on search term
  const filteredProjects = Array.isArray(projects)
    ? projects.filter(project => {
        const lowerSearchTerm = searchTerm.toLowerCase();
        
        // Search in basic fields
        const basicMatch = 
          project?.name?.toLowerCase().includes(lowerSearchTerm) ||
          project?.report_group_id?.toLowerCase().includes(lowerSearchTerm);
        
        // Search in keywords (unified system)
        const keywordMatch = 
          (project?.primary_keywords || []).some(keyword => 
            keyword.toLowerCase().includes(lowerSearchTerm)
          ) ||
          (project?.secondary_keywords || []).some(keyword => 
            keyword.toLowerCase().includes(lowerSearchTerm)
          ) ||
          (project?.minus_words || []).some(keyword => 
            keyword.toLowerCase().includes(lowerSearchTerm)
          ) ||
          // Legacy fallback
          (project?.keywords || []).some(keyword => 
            keyword.toLowerCase().includes(lowerSearchTerm)
          );
        
        return basicMatch || keywordMatch;
      })
    : [];

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    
    try {
      await projectService.deleteProject(projectToDelete.id);
      refetch();
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    } catch (err) {
      console.error('Error deleting project:', err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  // --- Provisioning actions (refresh / promote / attach) ---
  const closeActionDialog = () => {
    setActionDialog(null);
  };

  const runRefreshForum = async (project) => {
    setActionDialog({ kind: 'refresh', project, busy: true });
    try {
      const resp = await projectService.refreshForumStatus(project.id, null);
      if (resp && resp.success) {
        setActionDialog({
          kind: 'refresh',
          project,
          result: resp.data,
          busy: false,
        });
        refetch();
      } else {
        setActionDialog({
          kind: 'refresh',
          project,
          error: (resp && resp.error) || 'Refresh failed',
          busy: false,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Network error';
      setActionDialog({ kind: 'refresh', project, error: detail, busy: false });
    }
  };

  const promoteMutation = useMutation(
    ({ projectId, accountId, confirm }) =>
      projectService.promoteToMain(projectId, { account_id: accountId, confirm })
  );

  const runPromoteToMain = (project) => {
    setActionDialog({ kind: 'promote', project, plan: null, busy: true });
    // No-op fetch; we just open dialog with account picker.
    setActionDialog({ kind: 'promote', project, plan: null, busy: false });
  };

  const runPromoteDryRun = async (project, accountId) => {
    setActionDialog({ kind: 'promote', project, accountId, busy: true });
    try {
      const resp = await promoteMutation.mutateAsync({
        projectId: project.id,
        accountId,
        confirm: false,
      });
      if (resp && resp.success) {
        setActionDialog({
          kind: 'promote',
          project,
          accountId,
          plan: resp.data && resp.data.plan,
          busy: false,
        });
      } else {
        setActionDialog({
          kind: 'promote',
          project,
          accountId,
          error: (resp && resp.error) || 'Dry-run failed',
          busy: false,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Network error';
      setActionDialog({ kind: 'promote', project, accountId, error: detail, busy: false });
    }
  };

  const runPromoteConfirm = async (project, accountId) => {
    setActionDialog({ kind: 'promote', project, accountId, busy: true });
    try {
      const resp = await promoteMutation.mutateAsync({
        projectId: project.id,
        accountId,
        confirm: true,
      });
      if (resp && resp.success) {
        setActionDialog({
          kind: 'promote',
          project,
          accountId,
          result: resp.data,
          busy: false,
        });
        refetch();
      } else {
        setActionDialog({
          kind: 'promote',
          project,
          accountId,
          error: (resp && resp.error) || 'Promote failed',
          busy: false,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Network error';
      setActionDialog({ kind: 'promote', project, accountId, error: detail, busy: false });
    }
  };

  const attachMutation = useMutation(
    ({ projectId, accountId, parentProjectId, confirm }) =>
      projectService.attachToForum(projectId, {
        account_id: accountId,
        parent_project_id: parentProjectId,
        confirm,
      })
  );

  const runAttachToForum = (project) => {
    setActionDialog({ kind: 'attach', project, busy: false });
  };

  const runAttachDryRun = async (project, accountId, parentProjectId) => {
    setActionDialog({ kind: 'attach', project, accountId, parentProjectId, busy: true });
    try {
      const resp = await attachMutation.mutateAsync({
        projectId: project.id,
        accountId,
        parentProjectId,
        confirm: false,
      });
      if (resp && resp.success) {
        setActionDialog({
          kind: 'attach',
          project,
          accountId,
          parentProjectId,
          plan: resp.data && resp.data.plan,
          busy: false,
        });
      } else {
        setActionDialog({
          kind: 'attach',
          project,
          accountId,
          parentProjectId,
          error: (resp && resp.error) || 'Dry-run failed',
          busy: false,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Network error';
      setActionDialog({
        kind: 'attach',
        project,
        accountId,
        parentProjectId,
        error: detail,
        busy: false,
      });
    }
  };

  const runAttachConfirm = async (project, accountId, parentProjectId) => {
    setActionDialog({ kind: 'attach', project, accountId, parentProjectId, busy: true });
    try {
      const resp = await attachMutation.mutateAsync({
        projectId: project.id,
        accountId,
        parentProjectId,
        confirm: true,
      });
      if (resp && resp.success) {
        setActionDialog({
          kind: 'attach',
          project,
          accountId,
          parentProjectId,
          result: resp.data,
          busy: false,
        });
        refetch();
      } else {
        setActionDialog({
          kind: 'attach',
          project,
          accountId,
          parentProjectId,
          error: (resp && resp.error) || 'Attach failed',
          busy: false,
        });
      }
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Network error';
      setActionDialog({
        kind: 'attach',
        project,
        accountId,
        parentProjectId,
        error: detail,
        busy: false,
      });
    }
  };

  // Render keywords — show from categories if available, fallback to project-level
  const renderKeywords = (project) => {
    if (!project) {
      return <span className="text-gray-500">No keywords</span>;
    }

    // Priority: category_keywords_preview (from all categories) > project primary_keywords > legacy keywords
    const categoryKeywords = Array.isArray(project.category_keywords_preview) ? project.category_keywords_preview : [];
    const primaryKeywords = project.primary_keywords || [];
    const legacyKeywords = project.keywords || [];
    const categoryCount = project.category_count || 0;

    const displayKeywords = categoryKeywords.length > 0
      ? categoryKeywords
      : primaryKeywords.length > 0
        ? primaryKeywords.slice(0, 3)
        : legacyKeywords.slice(0, 3);

    if (displayKeywords.length === 0 && categoryCount === 0) {
      return <span className="text-gray-500">No keywords</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {displayKeywords.slice(0, 3).map((keyword, index) => (
          <Badge key={`kw-${index}`} variant="primary" size="sm">{keyword}</Badge>
        ))}
        {categoryCount > 0 && (
          <Badge variant="purple" size="sm" title={`${categoryCount} keyword categor${categoryCount === 1 ? 'y' : 'ies'}`}>
            {categoryCount} cat
          </Badge>
        )}
      </div>
    );
  };

  // Render provisioning type badge: forum project vs legacy/manual.
  // Forum project = bot_token + report_group_id + is_forum=true + WE OWN IT (owner_account_id + invite_link)
  // Legacy = bot_token + report_group_id but either not forum OR not managed by our farm
  // Manual = no bot_token / no report_group_id
  const renderProvisioningBadge = (project) => {
    if (!project) return null;
    const hasProvisioning = Boolean(project.bot_token && project.report_group_id);

    if (!hasProvisioning) {
      return <Badge variant="gray" size="sm">Manual</Badge>;
    }

    const isManagedForum = project.is_forum === true
      && project.owner_account_id != null
      && project.invite_link;

    if (!isManagedForum) {
      const label = project.is_forum === true
        ? 'Legacy (not ours)'
        : project.is_forum === false
          ? 'No forum'
          : 'Legacy';
      const tooltip = project.is_forum === true
        ? 'Group is a forum but not managed by our farm (no owner account or invite link)'
        : project.is_forum === false
          ? 'Group exists but Topics are not enabled'
          : 'Forum status not verified yet';
      return (
        <div className="flex flex-col gap-0.5">
          <Badge variant="amber" size="sm" title={tooltip}>
            <ExclamationTriangleIcon className="h-3 w-3 mr-1 inline" />
            {label}
          </Badge>
          {project.is_forum_checked_at && (
            <span className="text-xs text-gray-400" title="Last forum check">
              checked {new Date(project.is_forum_checked_at).toLocaleDateString()}
            </span>
          )}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-0.5">
        <Badge variant="green" size="sm" title="Owns its own forum group with Topics enabled, managed by our farm">
          <RocketLaunchIcon className="h-3 w-3 mr-1 inline" />
          Forum project
        </Badge>
        {project.is_forum_checked_at && (
          <span className="text-xs text-gray-400" title="Last forum check">
            checked {new Date(project.is_forum_checked_at).toLocaleDateString()}
          </span>
        )}
      </div>
    );
  };

  // Render topic link badge when project has report_topic_id
  const renderTopicBadge = (project) => {
    if (!project || !project.report_topic_id || !project.report_group_id) return null;
    const groupId = String(project.report_group_id).replace('-100', '');
    const url = `https://t.me/c/${groupId}/${project.report_topic_id}`;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-primary-600 hover:text-primary-900 hover:underline"
        title={`Open topic ${project.display_name || project.name} on Telegram`}
      >
        topic #{project.report_topic_id}
      </a>
    );
  };

  // Loading state
  if (isLoading) {
    return <LoadingState message="Loading projects..." />;
  }

  return (
    <div>
      <PageHeader
        title="Projects"
        description="Manage keyword-based message filtering and forwarding"
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              icon={RocketLaunchIcon}
              onClick={() => setAutoProvisionOpen(true)}
              title="Create bot + forum group + topic in one shot"
            >
              Auto-provision
            </Button>
            <Button
              variant="primary"
              icon={PlusIcon}
              to="/projects/new"
            >
              New Project
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="error" className="mb-4">
          {error.message || 'Failed to load projects'}
        </Alert>
      )}

      <Card>
        <div className="mb-4 flex justify-between items-center">
          <div className="relative w-full max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Search projects by name, report group ID, or keywords..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <Button
            variant="secondary"
            icon={ArrowPathIcon}
            onClick={() => refetch()}
            className="ml-3"
          >
            Refresh
          </Button>
        </div>

        {filteredProjects.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Keywords
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Report Group
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProjects.map((project) => (
                  <tr key={project?.id || Math.random()} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        <Link to={`/projects/${project?.id}`} className="hover:text-primary-600">
                          {project?.name || 'Unnamed Project'}
                        </Link>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderKeywords(project)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project?.report_group_id || 'Not set'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {renderProvisioningBadge(project)}
                        {renderTopicBadge(project)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={project?.is_active ? 'green' : 'gray'}>
                        {project?.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="inline-flex items-center space-x-3">
                        <Link
                          to={`/projects/${project?.id}`}
                          className="text-primary-600 hover:text-primary-900 inline-flex items-center"
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Edit
                        </Link>
                        <div className="relative">
                          <button
                            onClick={() => runRefreshForum(project)}
                            className="text-gray-500 hover:text-gray-700 inline-flex items-center"
                            title="Refresh forum status from Telegram"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => handleDeleteClick(project)}
                          className="text-red-600 hover:text-red-900 inline-flex items-center"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6">
            {searchTerm ? (
              <p className="text-gray-500">No projects found matching your search criteria.</p>
            ) : (
              <div>
                <p className="text-gray-500 mb-4">No projects have been created yet.</p>
                <Button
                  variant="primary"
                  icon={PlusIcon}
                  to="/projects/new"
                >
                  Create Project
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleDeleteCancel}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Delete Project
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the project "{projectToDelete?.name || 'this project'}"? 
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeleteCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Provisioning action dialog (refresh / promote / attach) */}
      {actionDialog && (
        <ProvisioningActionDialog
          accounts={accounts}
          dialog={actionDialog}
          onClose={closeActionDialog}
          onRefresh={(p) => runRefreshForum(p)}
          onPromoteDryRun={(p, accountId) => runPromoteDryRun(p, accountId)}
          onPromoteConfirm={(p, accountId) => runPromoteConfirm(p, accountId)}
          onAttachDryRun={(p, accountId, parentProjectId) => runAttachDryRun(p, accountId, parentProjectId)}
          onAttachConfirm={(p, accountId, parentProjectId) => runAttachConfirm(p, accountId, parentProjectId)}
        />
      )}

      {/* Auto-provision modal (creates bot + forum group + topic in one shot) */}
      <AutoProvisionModal
        isOpen={autoProvisionOpen}
        onClose={() => setAutoProvisionOpen(false)}
        onProvisioned={() => refetch()}
      />
    </div>
  );
};

// Dialog component for refresh / promote / attach actions.
// Always renders the action dialog if `dialog` is non-null. Calls back
// through on* props to the parent.
const ProvisioningActionDialog = ({
  accounts,
  dialog,
  onClose,
  onRefresh,
  onPromoteDryRun,
  onPromoteConfirm,
  onAttachDryRun,
  onAttachConfirm,
}) => {
  const [accountId, setAccountId] = useState('');
  const [parentProjectId, setParentProjectId] = useState('');

  // Reset account/parent selections when switching kinds/projects.
  useEffect(() => {
    setAccountId('');
    setParentProjectId('');
  }, [dialog && dialog.kind, dialog && dialog.project && dialog.project.id]);

  if (!dialog) return null;

  const project = dialog.project;
  const accountsList = (accounts && accounts.data) || accounts || [];

  let title = '';
  if (dialog.kind === 'refresh') title = 'Refresh forum status';
  if (dialog.kind === 'promote') title = `Promote "${project.name}" to Main`;
  if (dialog.kind === 'attach') title = `Attach "${project.name}" as subproject`;

  return (
    <div className="fixed z-20 inset-0 overflow-y-auto" aria-labelledby="action-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900" id="action-title">{title}</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">×</button>
            </div>

            {dialog.kind === 'refresh' && (
              <div className="space-y-3 text-sm">
                <p className="text-gray-600">
                  Reads is_forum from Telegram via Bot API getChat and caches it on this project.
                </p>
                {dialog.busy && <p className="text-blue-700">Refreshing...</p>}
                {dialog.error && <Alert variant="error">{dialog.error}</Alert>}
                {dialog.result && (
                  <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
                    is_forum = <strong>{String(dialog.result.isForum)}</strong>
                    {dialog.result.source && <> (source: {dialog.result.source})</>}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100">Close</button>
                  {!dialog.result && !dialog.busy && (
                    <button onClick={() => onRefresh(project)} className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">Refresh now</button>
                  )}
                </div>
              </div>
            )}

            {dialog.kind === 'promote' && (
              <div className="space-y-3 text-sm">
                <p className="text-gray-600">
                  Enable Topics on the group's report_group_id (if not already), promote the bot to admin,
                  refresh the invite link, and clear any parent linkage.
                </p>
                {!dialog.plan && (
                  <>
                    <label className="block">
                      <span className="text-xs text-gray-500">Owner account (required)</span>
                      <select
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                      >
                        <option value="">Select account...</option>
                        {accountsList.map((a) => (
                          <option key={a.id} value={a.id}>
                            #{a.id} — {a.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="flex justify-end gap-2">
                      <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100">Cancel</button>
                      <button
                        onClick={() => onPromoteDryRun(project, Number(accountId))}
                        disabled={!accountId || dialog.busy}
                        className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-300"
                      >
                        {dialog.busy ? 'Planning...' : 'Dry-run plan'}
                      </button>
                    </div>
                  </>
                )}
                {dialog.busy && !dialog.plan && !dialog.result && <p className="text-blue-700">Planning...</p>}
                {dialog.error && <Alert variant="error">{dialog.error}</Alert>}
                {dialog.plan && !dialog.result && (
                  <>
                    <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900 space-y-1">
                      <div><strong>Group:</strong> <span className="font-mono">{dialog.plan.reportGroupId}</span></div>
                      <div><strong>Account:</strong> #{dialog.plan.accountId}</div>
                      <div><strong>Enable forum:</strong> {String(dialog.plan.willEnableForum)}</div>
                      <div><strong>Promote bot:</strong> {String(dialog.plan.willPromoteBot)}</div>
                      <div><strong>Refresh invite link:</strong> {String(dialog.plan.willRefreshInviteLink)}</div>
                      <div><strong>Sibling projects in same group:</strong> {dialog.plan.siblingsCount}</div>
                      {dialog.plan.siblings && dialog.plan.siblings.length > 0 && (
                        <ul className="list-disc list-inside">
                          {dialog.plan.siblings.map((s) => (
                            <li key={s.id}>
                              #{s.id} {s.name} ({s.isSubproject ? 'subproject' : 'main'})
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onPromoteDryRun(project, Number(accountId))}
                        className="px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100"
                      >
                        Re-plan
                      </button>
                      <button
                        onClick={() => onPromoteConfirm(project, Number(accountId))}
                        disabled={dialog.busy}
                        className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300"
                      >
                        {dialog.busy ? 'Promoting...' : 'Confirm promote'}
                      </button>
                    </div>
                  </>
                )}
                {dialog.busy && dialog.plan && <p className="text-blue-700">Promoting...</p>}
                {dialog.result && (
                  <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800 space-y-1">
                    <div><strong>Project is now Main.</strong></div>
                    {dialog.result.inviteLink && (
                      <div>
                        Invite link:{' '}
                        <a href={dialog.result.inviteLink} target="_blank" rel="noopener noreferrer" className="underline">
                          {dialog.result.inviteLink}
                        </a>
                      </div>
                    )}
                    {dialog.result.siblings && dialog.result.siblings.length > 0 && (
                      <div>Existing siblings: {dialog.result.siblings.length}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {dialog.kind === 'attach' && (
              <div className="space-y-3 text-sm">
                <p className="text-gray-600">
                  Attach this project as a subproject of another project that already owns the same report_group_id.
                  A new topic will be created if needed.
                </p>
                {!dialog.plan && (
                  <>
                    <label className="block">
                      <span className="text-xs text-gray-500">Owner account (required)</span>
                      <select
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                      >
                        <option value="">Select account...</option>
                        {accountsList.map((a) => (
                          <option key={a.id} value={a.id}>
                            #{a.id} — {a.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-500">Parent project (optional — auto-detected)</span>
                      <input
                        type="number"
                        value={parentProjectId}
                        onChange={(e) => setParentProjectId(e.target.value)}
                        placeholder="Leave empty to auto-pick oldest other project in same group"
                        className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                      />
                    </label>
                    <div className="flex justify-end gap-2">
                      <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100">Cancel</button>
                      <button
                        onClick={() => onAttachDryRun(project, Number(accountId), parentProjectId ? Number(parentProjectId) : null)}
                        disabled={!accountId || dialog.busy}
                        className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-300"
                      >
                        {dialog.busy ? 'Planning...' : 'Dry-run plan'}
                      </button>
                    </div>
                  </>
                )}
                {dialog.busy && !dialog.plan && !dialog.result && <p className="text-blue-700">Planning...</p>}
                {dialog.error && <Alert variant="error">{dialog.error}</Alert>}
                {dialog.plan && !dialog.result && (
                  <>
                    <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900 space-y-1">
                      <div><strong>Parent:</strong> #{dialog.plan.parentProjectId} {dialog.plan.parentName}</div>
                      <div><strong>Group:</strong> <span className="font-mono">{dialog.plan.reportGroupId}</span></div>
                      <div><strong>Topic name:</strong> {dialog.plan.displayName}</div>
                      <div><strong>Create topic if missing:</strong> {String(dialog.plan.willCreateTopicIfMissing)}</div>
                      <div><strong>Mark as subproject:</strong> {String(dialog.plan.willMarkAsSubproject)}</div>
                      <div><strong>Parent is forum:</strong> {String(dialog.plan.forum && dialog.plan.forum.isForum)}</div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onAttachDryRun(project, Number(accountId), parentProjectId ? Number(parentProjectId) : null)}
                        className="px-3 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100"
                      >
                        Re-plan
                      </button>
                      <button
                        onClick={() => onAttachConfirm(project, Number(accountId), parentProjectId ? Number(parentProjectId) : null)}
                        disabled={dialog.busy}
                        className="px-3 py-1.5 text-sm rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-300"
                      >
                        {dialog.busy ? 'Attaching...' : 'Confirm attach'}
                      </button>
                    </div>
                  </>
                )}
                {dialog.busy && dialog.plan && <p className="text-blue-700">Attaching...</p>}
                {dialog.result && (
                  <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800 space-y-1">
                    <div><strong>Attached.</strong></div>
                    <div>Parent: #{dialog.result.parentProjectId} {dialog.result.parentName}</div>
                    {dialog.result.reportTopicId && <div>Topic: #{dialog.result.reportTopicId}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;
